import styles from './styles.scss';
import { rm, NO_ELEMENT, scrollToTarget } from 'utils';
import { useEffect, useReducer, useRef } from 'react';
import { useToggle } from 'hooks';
import { postSendProductQuestion } from 'services/ApiCustomerSupportFormService';
import { Input, TextArea } from 'components/Shared/Form';
import { Button, LoaderButton } from 'components/Button';
import { IBasicResponse } from 'models/IBasicResponse';
import {
  BlueSectionContainer,
  ErrorContainer,
  SubmitContainer
} from 'components/CustomerSupportForms/Components/SectionContainer/SectionContainer';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { isValid, productQuestionInitialState, reducer } from './ProductQuestionHelper';
import { DynamicImageUploader } from '../Components/ImageUpload/DynamicImageUploader';
import InputWithRules from 'components/Input/Input';
import { contactOptionChanged, emailRule, nameRule, phoneRule, OrderFormProps } from '../Utils';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';

const QUESTION_MAX_LENGTH = 1000;
const MAX_IMAGE_COUNT = 5;

export const ProductQuestionForm = ({ error, returnUrl, onSuccess }: OrderFormProps) => {
  const anchorRef = useRef(NO_ELEMENT);
  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [internalState, internalDispatch] = useReducer(reducer, productQuestionInitialState);
  const [loading, toggleLoading] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [error]);

  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });
    internalDispatch({ type: 'addressError', value: '' });

    if (isValid(internalState, internalDispatch) === false) {
      toggleLoading();
      return Promise.resolve();
    }

    const { images, name, email, question, phone, subject, contactOption } = internalState;

    return postSendProductQuestion({
      files: images.map(img => img.imageUrl),
      question,
      name,
      phone,
      subject,
      email,
      customerContactOption: contactOption
    })
      .then(() => {
        onSuccess();
        scrollToTopIfNeeded();
        reduxDispatch(GtmCustomerSupportFormSubmit(FormType.ProductQuestion));
      })
      .catch((response: IBasicResponse) => {
        internalDispatch({ type: 'generalError', value: response.message[0] });
      })
      .finally(() => {
        toggleLoading();
      });
  };

  return (
    <>
      <h3>{rm('Customerservice_Form_Product_Contact_Header')}</h3>
      <BlueSectionContainer error={internalState.addressError}>
        <div className={styles.contactColumn}>
          <div>
            <InputWithRules
              change={e => internalDispatch({ type: 'name', value: e.target.value ?? '' })}
              value={internalState.name}
              type="text"
              rule={nameRule}
              placeholder={rm('Customerservice_Form_Product_Contact_Name')}
            />
          </div>

          <div>
            <InputWithRules
              change={e => internalDispatch({ type: 'phone', value: e.target.value ?? '' })}
              value={internalState.phone}
              type="tel"
              rule={phoneRule}
              placeholder={rm('Customerservice_Form_Product_Contact_Phone')}
              inputMode="tel"
            />
          </div>
        </div>

        <div>
          <InputWithRules
            change={e => internalDispatch({ type: 'email', value: e.target.value ?? '' })}
            value={internalState.email}
            type="email"
            rule={emailRule}
            placeholder={rm('Customerservice_Form_Product_Contact_Email')}
          />
        </div>
      </BlueSectionContainer>
      {internalState.addressError && <ErrorContainer>{internalState.addressError}</ErrorContainer>}
      <h3>{rm('Customerservice_Form_Product_Question_Header')}</h3>
      <BlueSectionContainer>
        <div className={styles.questionWrapper}>
          <div>
            <strong>{rm('Customerservice_Form_Product_Question_Subject')}</strong>
            <Input
              onChange={(_event, newValue) =>
                internalDispatch({ type: 'subject', value: newValue ?? '' })
              }
              value={internalState.subject}
            />
          </div>
          <div>
            <TextArea
              placeholder={rm('Customerservice_Form_Product_Question_Placeholder')}
              onChange={(_event, newValue) =>
                internalDispatch({ type: 'question', value: newValue ?? '' })
              }
              textlimit={QUESTION_MAX_LENGTH}
              value={internalState.question}
            />
            <p
              id={styles.returnMsgLength}
            >{`(${internalState.question.length}/${QUESTION_MAX_LENGTH})`}</p>
          </div>
        </div>
      </BlueSectionContainer>

      <BlueSectionContainer error={internalState.imagesError}>
        <div className={styles.uploadHeaderContainer}>
          <strong>{rm('customerpage_returns_images_header')}</strong>
          <p>{rm('CustomerService_Return_Optional_Label')}</p>
        </div>
        <DynamicImageUploader
          images={internalState.images}
          maxImageCount={MAX_IMAGE_COUNT}
          dispatch={internalDispatch}
        />
      </BlueSectionContainer>
      <CustomerContact
        contact={internalState.contactOption}
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
        addressAlreadyAdded={true}
      />
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>

      {internalState.generalError && (
        <BlueSectionContainer error>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
